/*
  Вкладка «Подсудность домов» - решение владельца 02.11.2026, вариант А макетов.

  Зачем. С общим справочником платформы участок дому находит Арбитр ЖКХ, а от вас
  нужен протокол общего собрания: без него заявление не собирается. До этой вкладки
  дому, участок которого найден не вашей записью, протокол было вносить некуда, а
  справочник звал его «не привязанным».

  Сверху вниз, как в макете: пояснение, отбор (адрес, участок, населённый пункт),
  вкладки-состояния со счётчиками, таблица по 25 домов. Все счёты и страницы - с
  сервера по всему портфелю (`useCourtHouses`).

  Действия переиспользуют прежние окна, а не заводят свои:
  - «Внести протокол» - то же окно, что у дома участка (`ProtocolDialog`). Дому без
    вашей записи она заводится первым шагом (`POST /court-houses`): протокол хранится
    в ней, а участок остаётся из общего справочника;
  - «Другой участок» и «Указать участок» - мастер привязки (`BindWizard`): заявление
    пойдёт в участок, который укажете вы; внесённый протокол при этом остаётся;
  - «Изменить» - та же форма основания и полного адреса (`BasisForm`).

  Отказ сервера показывается у действия: в окне - внутри окна, у формы - под формой,
  у кнопки строки - над таблицей рядом со строками (правило 3 скилла `frontend`).
*/

import { useState } from 'react'

import { api } from '../api/client.js'
import { plural } from '../api/format.js'
import BindWizard from './BindWizard.jsx'
import { BasisForm } from './CourtBuildingBasis.jsx'
import CourtHouseFilters from './CourtHouseFilters.jsx'
import CourtHouseRow from './CourtHouseRow.jsx'
import { Loading, Notice } from './Common.jsx'
import PageBar from './PageBar.jsx'
import ProtocolDialog from './ProtocolStep.jsx'
import Tabs, { TabPanel } from './Tabs.jsx'
import useCourtHouses, { ALL } from './useCourtHouses.js'

const STATES = [
  { key: ALL, title: 'Все' },
  { key: 'no_protocol', title: 'Нет протокола' },
  { key: 'disputed', title: 'Участок уточняется' },
  { key: 'unchecked', title: 'Ещё не проверен' },
  { key: 'ready', title: 'Готово к заявлению' },
]

const EMPTY_FILTER = { q: '', court: '', settlement: '' }

export default function CourtHousesTab({ sections, version, onChanged }) {
  const [filter, setFilter] = useState(EMPTY_FILTER)
  const [state, setState] = useState(ALL)
  const [protocolFor, setProtocolFor] = useState(null)
  const [editing, setEditing] = useState(null)
  const [binding, setBinding] = useState(null)
  const [failure, setFailure] = useState(null)
  const list = useCourtHouses({ ...filter, state }, version)
  const answer = list.answer

  async function openProtocol(house) {
    setFailure(null)
    try {
      // Дому без вашей записи она заводится сейчас: протокол хранится в ней.
      setProtocolFor(
        house.building_id
          ? asBuilding(house)
          : await api.claimCourtHouse({ address: house.address, full_address: house.full_address }),
      )
    } catch (error) {
      setFailure(error)
    }
  }

  async function saveBasis(values) {
    setFailure(null)
    try {
      await api.setHouseManagement(editing.building_id, values)
      setEditing(null)
      onChanged()
    } catch (error) {
      setFailure(error)
    }
  }

  async function attach(sectionId, { address, management, ...extra }) {
    await api.attachBuilding(sectionId, address, management ?? {}, extra)
    onChanged()
  }

  const tabs = STATES.map((one) => ({
    ...one,
    badge: answer ? String(answer.counts[one.key] ?? 0) : null,
    badgeTitle: 'домов',
  }))

  return (
    <>
      <p className="muted jurisdiction__lead">
        Участок для каждого дома определяется по его адресу. От вас нужен протокол общего
        собрания: без него заявление не собирается. Если участок назван неверно - «Другой
        участок»: заявление пойдёт в тот, что укажете вы.
      </p>
      <WithoutHouse homeless={answer?.no_house} />
      <CourtHouseFilters
        filter={filter}
        courts={answer?.courts ?? []}
        settlements={answer?.settlements ?? []}
        onChange={setFilter}
      />
      <Tabs tabs={tabs} active={state} onChange={setState} nested />

      {/* Панель выбранной вкладки-состояния: таблица одна, меняется отбор. */}
      <TabPanel tabKey={state} active={state}>
      <section className="card">
        <Notice error={failure ?? list.error} />
        {list.rows === null && !list.error ? <Loading what="Собираем дома портфеля" /> : null}
        {list.rows?.length ? (
          <>
            <table className="table table--jurisdiction table--fixed">
              <thead>
                <tr>
                  <th>Дом</th>
                  <th className="table__num">Помещений</th>
                  <th>Участок</th>
                  <th>Протокол собрания</th>
                  <th> </th>
                </tr>
              </thead>
              <tbody>
                {list.rows.map((house) => (
                  <CourtHouseRow
                    key={house.building_id ?? house.address}
                    house={house}
                    onProtocol={openProtocol}
                    onEdit={(chosen) => setEditing(chosen)}
                    onBind={(chosen) => setBinding(chosen)}
                  />
                ))}
              </tbody>
            </table>
            <PageBar {...list.bar} what="домов" />
          </>
        ) : null}
        {list.rows?.length === 0 ? <p className="muted">{emptyText(state, filter)}</p> : null}

        {editing ? (
          <BasisForm
            building={asBuilding(editing)}
            onCancel={() => setEditing(null)}
            onSave={saveBasis}
          />
        ) : null}
      </section>
      </TabPanel>

      {protocolFor ? (
        <ProtocolDialog
          building={protocolFor}
          onSave={(values) => api.setHouseManagement(protocolFor.id, values)}
          onReadScan={(file) => api.readHouseProtocolScan(protocolFor.id, file)}
          onSaveScan={(file, values, replace) =>
            api.saveHouseProtocolScan(protocolFor.id, file, values, replace)
          }
          onClose={() => {
            setProtocolFor(null)
            onChanged()
          }}
        />
      ) : null}

      {binding ? (
        <BindWizard
          house={binding}
          fullAddress={binding.full_address}
          askFullAddress={binding.needs_full_address}
          sections={sections}
          sectionId={null}
          onAttach={attach}
          onClose={() => setBinding(null)}
        />
      ) : null}
    </>
  )
}

/*
  Помещения, в адресе которых нет дома («Кв. 64»), - одной строкой, а не домами.
  Решение владельца 03.11.2026: первая проверка в бою показала в демо-кабинете 382
  таких «дома», каждый «ещё не проверен» навсегда. Отборам строка не подчиняется:
  она про весь портфель, как предупреждение над вкладками.
*/
function WithoutHouse({ homeless }) {
  if (!homeless?.premises) return null
  const example = homeless.example ? ` (например, «${homeless.example}»)` : ''
  return (
    <Notice kind="warn" wide>
      <div>
        Адрес без дома: {plural(homeless.premises, 'помещение', 'помещения', 'помещений')}.
      </div>
      <div className="muted">
        В адресе записан только номер помещения{example}, без населённого пункта, улицы и
        дома. Участок по такому адресу не определить, и заявления по этим помещениям не
        соберутся. Адрес нужно исправить там, откуда приходят счета: в учётной программе
        или в файле выгрузки.
      </div>
    </Notice>
  )
}

/* Строка вкладки в том виде, в каком её ждут окна дома участка (`BuildingOut`). */
function asBuilding(house) {
  return { ...house, id: house.building_id }
}

function emptyText(state, filter) {
  if (filter.q || filter.court || filter.settlement || state !== ALL) {
    return 'Под этот отбор домов нет. Сбросьте отборы или выберите вкладку «Все».'
  }
  return 'Домов пока нет: они появятся, когда в портфель будут загружены лицевые счета.'
}
